import type { LoginDTO } from "../../domain/dto/LoginDTO";
import type { AuthRepository } from "../../domain/repositories/AuthRepository";
import type { SessionPort } from "../../domain/ports/SessionPort";
import type { User } from "../../domain/entities/User";
import {
  OTP_PREFERENCE_EMAIL,
} from "../../domain/constants/otpConstants";

export interface LoginResult {
  user: User;
  nextStep:
    | "setup-password"
    | "setup-twofa"
    | "verification-code"
    | "email-verification"
    | "complete";
}

const isPreferenceSetupPending = (user: User): boolean => !user.otp_preference;

const routeOtpVerification = (user: User): LoginResult["nextStep"] =>
  user.otp_preference === OTP_PREFERENCE_EMAIL
    ? "email-verification"
    : "verification-code";

const requiresPendingMfaSession = (
  nextStep: LoginResult["nextStep"],
): boolean =>
  nextStep === "email-verification" || nextStep === "verification-code";

export const loginUser = async (
  repo: AuthRepository,
  sessionPort: SessionPort,
  data: LoginDTO,
): Promise<LoginResult> => {
  const user = await repo.login(data);

  let nextStep: LoginResult["nextStep"];

  if (!user.is_password_set) {
    nextStep = "setup-password";
  } else if (isPreferenceSetupPending(user)) {
    if (user.is_totp_enabled && user.temp_access_token) {
      nextStep = routeOtpVerification(user);
    } else {
      nextStep = "setup-twofa";
    }
  } else if (user.temp_access_token) {
    nextStep = routeOtpVerification(user);
  } else {
    nextStep = "complete";
  }

  if (requiresPendingMfaSession(nextStep)) {
    sessionPort.savePendingMfaSession(user);
  } else {
    sessionPort.saveSession(user);
  }

  return { user, nextStep };
};
